import { Controller } from "@hotwired/stimulus"
import { debug } from "../debug"

// NEW VERSION WITH NO POPUPS OR ALERTS WHATSOEVER
debug('New booking iframe controller with no alerts loaded');

/**
 * Booking iframe loader controller - no alerts or popups
 * 
 * This controller handles the loading states for an iframe booking widget.
 * It displays a loading indicator, shows the iframe once loaded, and 
 * provides fallback error handling if the iframe fails to load.
 */
export default class extends Controller {
  static targets = ["iframe", "loading", "error"]
  static values = {
    timeout: { type: Number, default: 15000 } // 15 seconds timeout
  }

  // NO ALERTS! Simple connect method
  connect() {
    debug("%c FRESHLY UPDATED booking_iframe_controller.js: connect() called - NO ALERT SHOULD BE HERE! Version: TIMESTAMP_20250511_1938", "color: #00FF00; font-size: 16px; font-weight: bold;");
    debug("BookingIframe: Connected")
    
    // Initialize if all targets exist
    if (this.hasIframeTarget && this.hasLoadingTarget && this.hasErrorTarget) {
      this.initialize()
    }
  }

  // Initialize controller - no alerts or popups
  initialize() {
    // Show loading state first
    this.showLoading()
    
    // Clear any existing timeouts
    this.clearTimeouts()
    
    // Set up new timeouts
    this.setupTimeouts()
  }
  
  // Handle iframe loaded event
  iframeLoaded() {
    this.clearTimeouts()
    this.showIframe()
    // Removed resizeIframe() - using fixed CSS height instead
  }

  // Dynamically resize iframe based on content height
  resizeIframe() {
    const iframe = this.iframeTarget

    try {
      // Try to access iframe content height
      const iframeDoc = iframe.contentDocument || iframe.contentWindow.document

      if (iframeDoc) {
        // Get the actual content height
        const contentHeight = Math.max(
          iframeDoc.body.scrollHeight,
          iframeDoc.documentElement.scrollHeight,
          iframeDoc.body.offsetHeight,
          iframeDoc.documentElement.offsetHeight
        )

        debug(`Iframe content height: ${contentHeight}px`)

        // Use CSS classes instead of inline styles (CSP-friendly)
        if (contentHeight <= 1400) {
          iframe.classList.add('iframe-height-mobile')
        } else {
          iframe.classList.add('iframe-height-desktop')
        }
      }
    } catch (error) {
      // Cross-origin issues - can't access iframe content
      // Fall back to safe default height for cross-origin iframes
      debug('Cannot access iframe content (cross-origin). Using safe default height.', error)

      // Use CSS class instead of inline style (CSP-friendly)
      // Desktop gets 1800px, mobile gets 1400px via CSS media query
      iframe.classList.add('iframe-height-desktop')

      // Still set up postMessage listener in case fiumed.hr sends height later
      this.setupPostMessageListener()
    }
  }

  // Set up postMessage listener for cross-origin iframe resizing
  setupPostMessageListener() {
    this.messageHandler = (event) => {
      // Verify the origin (adjust to match your fiumed.hr domain)
      if (event.origin !== 'https://fiumed.hr') {
        return
      }

      // Check if it's a height message
      if (event.data && event.data.type === 'iframeHeight') {
        const height = parseInt(event.data.height, 10)
        if (height && !isNaN(height)) {
          debug(`Received height from iframe: ${height}px`)

          // Remove existing height classes
          this.iframeTarget.classList.remove('iframe-height-mobile', 'iframe-height-desktop')

          // Choose appropriate class based on received height
          if (height <= 1400) {
            this.iframeTarget.classList.add('iframe-height-mobile')
          } else {
            this.iframeTarget.classList.add('iframe-height-desktop')
          }
        }
      }
    }

    window.addEventListener('message', this.messageHandler)
  }
  
  // Handle retry button click
  retry(event) {
    event.preventDefault()
    
    // Reset iframe src
    const currentSrc = this.iframeTarget.src
    this.iframeTarget.src = ""
    
    // Small delay before reloading
    setTimeout(() => {
      this.iframeTarget.src = currentSrc
      this.initialize()
    }, 100)
  }
  
  // Show loading state
  showLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove('hidden')
    }

    if (this.hasErrorTarget) {
      this.errorTarget.classList.add('hidden')
    }

    if (this.hasIframeTarget) {
      this.iframeTarget.classList.add('hidden')
    }
  }

  // Show iframe
  showIframe() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add('hidden')
    }

    if (this.hasErrorTarget) {
      this.errorTarget.classList.add('hidden')
    }

    if (this.hasIframeTarget) {
      this.iframeTarget.classList.remove('hidden')
    }
  }

  // Show error state
  showError() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add('hidden')
    }

    if (this.hasErrorTarget) {
      this.errorTarget.classList.remove('hidden')
    }

    if (this.hasIframeTarget) {
      this.iframeTarget.classList.add('hidden')
    }
  }
  
  // Set up timeouts
  setupTimeouts() {
    // Show iframe after a short delay
    this.shortTimeout = setTimeout(() => {
      this.showIframe()
    }, 3000)
    
    // Show error if it takes too long
    this.loadTimeout = setTimeout(() => {
      this.showError()
    }, this.timeoutValue)
  }
  
  // Clear timeouts
  clearTimeouts() {
    if (this.shortTimeout) {
      clearTimeout(this.shortTimeout)
    }
    
    if (this.loadTimeout) {
      clearTimeout(this.loadTimeout)
    }
    
    this.shortTimeout = null
    this.loadTimeout = null
  }
  
  // Clean up on disconnect
  disconnect() {
    this.clearTimeouts()

    // Remove message listener if it exists
    if (this.messageHandler) {
      window.removeEventListener('message', this.messageHandler)
      this.messageHandler = null
    }
  }
}